import React, { useState, useEffect, useMemo } from 'react';
import adminService from '../services/admin.service';
import authService from '../services/auth.service';
import { Paper, Typography, IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, TextField, Box, Avatar, Chip, Stack, CircularProgress, InputAdornment } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GppBadIcon from '@mui/icons-material/GppBad';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';

import AdminUserDetailDrawer from './AdminUserDetailDrawer';
import AdminUserFilterDrawer from './AdminUserFilterDrawer';
import AdminUserSortDrawer from './AdminUserSortDrawer';


const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length > 1) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    } else if (nameParts[0] && nameParts[0].length > 1) {
        return nameParts[0].substring(0, 2).toUpperCase();
    }
    return (name[0] || '').toUpperCase();
};

const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRole, setSelectedRole] = useState('All');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedSort, setSelectedSort] = useState('default');

    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [isSortDrawerOpen, setSortDrawerOpen] = useState(false);
    const [isDetailDrawerOpen, setDetailDrawerOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);


    useEffect(() => {
        const user = authService.getCurrentUser();
        setCurrentUser(user);

        adminService.getAllUsers().then(
            response => {
                setUsers(response.data);
                setLoading(false);
            },
            error => {
                const _error = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
                setError(_error);
                setLoading(false);
            }
        );
    }, []);

    const handleOpenDetailDrawer = (user) => {
        setSelectedUser(user);
        setDetailDrawerOpen(true);
    };

    const handleCloseDetailDrawer = () => {
        setDetailDrawerOpen(false);
        setSelectedUser(null);
    };

    const handleUserDeleted = (userId) => {
        setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
        handleCloseDetailDrawer();
    };

    const handleUserUpdated = (updatedUser) => {
        setUsers(prevUsers =>
            prevUsers.map(u => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u))
        );
        setSelectedUser(prevSelected => prevSelected ? { ...prevSelected, ...updatedUser } : null);
    };

    const handleApplyFilters = (role, status) => {
        setSelectedRole(role);
        setSelectedStatus(status);
        setFilterDrawerOpen(false);
    };

    const handleApplySort = (sortValue) => {
        setSelectedSort(sortValue);
        setSortDrawerOpen(false);
    };

    const filteredAndSortedUsers = useMemo(() => {
        let filtered = users.filter(user => {
            const matchesSearch = !searchQuery ||
                user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (user.company && user.company.toLowerCase().includes(searchQuery.toLowerCase()));

            const matchesRole = selectedRole === 'All' || user.role === selectedRole;

            const matchesStatus = selectedStatus === 'All' ||
                (selectedStatus === 'verified' && user.is_verified) ||
                (selectedStatus === 'not_verified' && !user.is_verified);

            return matchesSearch && matchesRole && matchesStatus;
        });

        switch (selectedSort) {
            case 'username_asc':
                return filtered.sort((a, b) => a.username.localeCompare(b.username));
            case 'username_desc':
                return filtered.sort((a, b) => b.username.localeCompare(a.username));
            case 'role':
                return filtered.sort((a, b) => a.role.localeCompare(b.role));
            default:
                return filtered;
        }
    }, [users, searchQuery, selectedRole, selectedStatus, selectedSort]);

    const sortLabels = {
        default: 'Default',
        username_asc: 'Username (A-Z)',
        username_desc: 'Username (Z-A)',
        role: 'Role',
    };
    const getSortLabel = (key) => sortLabels[key] || key;


    if (loading) {
        return <CircularProgress sx={{ display: 'block', margin: 'auto' }} />;
    }

    return (
        <Box sx={{ p: 3, margin: 'auto', maxWidth: 1200, bgcolor: 'background.default', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 3, sm: 4 } }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        User Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1D4ED8', fontWeight: 400 }}>
                        Manage all registered users
                    </Typography>
                </Box>
            </Box>

            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by username, email, or company..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '16px',
                        backgroundColor: 'white',
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start" sx={{ ml: 1 }}>
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        onClick={() => setFilterDrawerOpen(true)}
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        sx={{
                            borderRadius: '16px', bgcolor: '#fff', borderColor: '#E8EBF0', color: 'text.primary',
                            ...((selectedRole !== 'All' || selectedStatus !== 'All') && { bgcolor: '#2563EB', color: 'white', '&:hover': { bgcolor: '#1D4ED8' } })
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        onClick={() => setSortDrawerOpen(true)}
                        variant="outlined"
                        startIcon={<SortIcon />}
                        sx={{
                            borderRadius: '16px', bgcolor: '#fff', borderColor: '#E8EBF0', color: 'text.primary',
                            ...(selectedSort !== 'default' && { bgcolor: '#2563EB', color: 'white', '&:hover': { bgcolor: '#1D4ED8' } })
                        }}
                    >
                        Sort
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {searchQuery && (<Chip label={`Search: "${searchQuery}"`} onDelete={() => setSearchQuery('')} />)}
                {selectedRole !== 'All' && (<Chip label={`Role: ${selectedRole}`} onDelete={() => setSelectedRole('All')} />)}
                {selectedStatus !== 'All' && (<Chip label={`Status: ${selectedStatus.replace('_', ' ')}`} onDelete={() => setSelectedStatus('All')} />)}
                {selectedSort !== 'default' && (<Chip label={`Sort: ${getSortLabel(selectedSort)}`} onDelete={() => setSelectedSort('default')} />)}
            </Box>


            {error ? (
                <div className="alert alert-danger" role="alert">{error}</div>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                        gap: 3,
                    }}
                >
                    {filteredAndSortedUsers.map(user => (
                        <Paper
                            key={user.id}
                            onClick={() => handleOpenDetailDrawer(user)}
                            sx={{
                                p: 2.5,
                                borderRadius: 4,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                                    cursor: 'pointer'
                                },
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar sx={{ bgcolor: '#2563EB', color: '#ffffff', width: 50, height: 50, fontSize: '1.2rem' }}>
                                        {getInitials(user.username)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{user.username}</Typography>
                                        <Typography variant="body2" color="text.secondary">{user.company || 'No company'}</Typography>
                                    </Box>
                                </Stack>
                                <Tooltip title={currentUser?.username === user.username ? "You cannot delete your own account" : "Delete User"}>
                                    <span>
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // The delete logic is now in the drawer, this is just for show
                                            }}
                                            color="error"
                                            disabled={currentUser?.username === user.username}
                                            sx={{ visibility: 'hidden' }} // Keep for layout, but delete is in drawer
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                            <Stack spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Chip
                                        icon={user.is_verified ? <VerifiedUserIcon /> : <GppBadIcon />}
                                        label={user.is_verified ? "Verified" : "Not Verified"}
                                        color={user.is_verified ? "success" : "default"}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        icon={user.role === 'admin' ? <AdminPanelSettingsIcon /> : <PersonIcon />}
                                        label={user.role}
                                        color={user.role === 'admin' ? "primary" : "default"}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                    {filteredAndSortedUsers.length === 0 && (
                        <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary', gridColumn: '1 / -1' }}>
                            No users found matching your criteria.
                        </Typography>
                    )}
                </Box>
            )}

            <AdminUserFilterDrawer
                open={isFilterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                selectedRole={selectedRole}
                selectedStatus={selectedStatus}
                onApplyFilters={handleApplyFilters}
            />

            <AdminUserSortDrawer
                open={isSortDrawerOpen}
                onClose={() => setSortDrawerOpen(false)}
                selectedSort={selectedSort}
                onApplySort={handleApplySort}
            />

            <AdminUserDetailDrawer
                user={selectedUser}
                open={isDetailDrawerOpen}
                onClose={handleCloseDetailDrawer}
                onUserDeleted={handleUserDeleted}
                onUserUpdated={handleUserUpdated}
            />

        </Box>
    );
};

export default AdminUserManagement; 